/**
 * Track claim page — /claim/track/:token (#1066)
 *
 * Public landing for the email-track-intake pipeline: a sailor emailed their
 * VKX export to uploads@regattareplay.com and received a claim link. Here they
 * confirm "this is my boat", accept the ToS, and get logged in (email already
 * verified by the email loop — the claim response returns a JWT).
 *
 * Mobile-first single column; mirrors the InviteLanding visual language.
 */

import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { formatSailNumber } from '@regatta-replay/types';
import { api, ApiError } from '@/lib/api';
import {
  formatTrackDate,
  formatTrackDuration,
  type TrackClaimEntry,
  type TrackClaimResponse,
  type TrackClaimResult,
} from '@/lib/track-claim';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import {
  PhoneVerificationStep,
  type PhoneVerificationResult,
} from '@/components/claim/PhoneVerificationStep';

type PageState =
  | 'loading'
  | 'ready'      // status 'pending' — show the claim form
  | 'claiming'   // POST in flight
  | 'success'    // claim accepted
  | 'claimed'    // GET said already claimed
  | 'expired'    // GET said expired
  | 'invalid'    // 404 — bad token
  | 'error';     // GET network/server failure

/** Which affordance the "This is you?" section is showing. */
type EntryStep = 'confirm' | 'picker' | 'chosen';

const SUPPORT_EMAIL = 'hello@regattareplay.com';

function EntryCard({ entry }: { entry: TrackClaimEntry }) {
  return (
    <div className="text-center">
      <p className="text-3xl font-bold tracking-tight text-navy-900">
        {formatSailNumber(entry.sailNumber)}
      </p>
      <p className="mt-1 text-base font-medium text-navy-900">{entry.boatName}</p>
      <p className="text-sm text-muted-foreground">{entry.skipperName}</p>
    </div>
  );
}

export function TrackClaimPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);

  const [pageState, setPageState] = useState<PageState>('loading');
  const [claim, setClaim] = useState<TrackClaimResponse | null>(null);
  const [entryStep, setEntryStep] = useState<EntryStep>('confirm');
  const [selectedEntryId, setSelectedEntryId] = useState<string | null>(null);
  const [entryFilter, setEntryFilter] = useState('');
  const [acceptTos, setAcceptTos] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [result, setResult] = useState<TrackClaimResult | null>(null);
  // Phone-verification gate (SPEC-Phone-Verification-Claim-Flow §6).
  // The step is optional from the frontend — the backend enforces when
  // REQUIRE_PHONE_VERIFY is on. phoneVerify starts null (no interaction yet).
  const [phoneVerify, setPhoneVerify] = useState<PhoneVerificationResult | null>(null);

  // Load the claim on mount
  useEffect(() => {
    if (!token) {
      setPageState('invalid');
      return;
    }

    let cancelled = false;

    async function load() {
      try {
        const data = await api.get<TrackClaimResponse>(`/track-claims/${token}`, {
          silent: true,
        });
        if (cancelled) return;

        setClaim(data);
        if (data.status === 'claimed') {
          setPageState('claimed');
        } else if (data.status === 'expired') {
          setPageState('expired');
        } else {
          // No auto-matched entry → go straight to the picker
          setEntryStep(data.match.entry ? 'confirm' : 'picker');
          setPageState('ready');
        }
      } catch (err) {
        if (cancelled) return;
        setPageState(err instanceof ApiError && err.status === 404 ? 'invalid' : 'error');
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const matchedEntry = claim?.match.entry ?? null;
  const hasRegatta = !!claim?.match.regatta;

  const selectedEntry = useMemo(() => {
    if (!claim || !selectedEntryId) return null;
    return (
      claim.entryOptions.find((e) => e.entryId === selectedEntryId) ??
      (matchedEntry?.entryId === selectedEntryId ? matchedEntry : null)
    );
  }, [claim, selectedEntryId, matchedEntry]);

  const filteredOptions = useMemo(() => {
    if (!claim) return [];
    const q = entryFilter.trim().toLowerCase();
    if (!q) return claim.entryOptions;
    return claim.entryOptions.filter((e) =>
      `${e.sailNumber} ${e.boatName} ${e.skipperName}`.toLowerCase().includes(q)
    );
  }, [claim, entryFilter]);

  // With a regatta match, an entry must be confirmed/picked before submitting.
  // Without one, our team matches manually and the claim proceeds entry-less.
  const entryReady = !hasRegatta || selectedEntryId !== null;
  const canSubmit = acceptTos && entryReady && pageState === 'ready';

  async function handleConfirm() {
    if (!token || !canSubmit) return;

    setSubmitError('');
    setPageState('claiming');
    try {
      const res = await api.post<TrackClaimResult>(
        `/track-claims/${token}/claim`,
        {
          entryId: hasRegatta ? selectedEntryId : null,
          acceptTos: true,
          ...(displayName.trim() ? { displayName: displayName.trim() } : {}),
          // Phone-verification gate fields (present only if user verified phone)
          ...(phoneVerify?.verifyToken ? { verifyToken: phoneVerify.verifyToken } : {}),
          ...(phoneVerify?.notifyOptIn ? { notifyOptIn: true } : {}),
          ...(phoneVerify?.notifyConsentText ? { notifyConsentText: phoneVerify.notifyConsentText } : {}),
        },
        { silent: true }
      );

      // Log in the same way the invite flow does (auth store persists the
      // JWT). The claim response carries no profile object, so store a
      // provisional one and hydrate the real profile via /auth/validate.
      login(res.token, {
        userId: res.userId,
        email: '',
        name: displayName.trim() || selectedEntry?.skipperName || '',
        role: 'user',
        createdAt: new Date().toISOString(),
      });
      void useAuthStore.getState().validateSession();

      setResult(res);
      setPageState('success');
    } catch (err) {
      // Non-destructive: back to the form with state intact
      setPageState('ready');
      if (err instanceof ApiError && err.status === 410) {
        setSubmitError(
          'This link has expired or the track was already claimed. ' +
            `If that doesn't sound right, email ${SUPPORT_EMAIL}.`
        );
      } else if (err instanceof ApiError && err.status === 400) {
        const msg = (err as ApiError & { body?: { message?: string } }).body?.message ?? '';
        if (msg.toLowerCase().includes('phone verification')) {
          setSubmitError('Phone verification is required. Please verify your phone number above.');
          setPhoneVerify(null); // Reset so the step re-renders
        } else {
          setSubmitError('Please accept the Terms of Service to continue.');
        }
      } else {
        setSubmitError("Something went wrong — nothing was lost. Please try again.");
      }
    }
  }

  function goToRedirect() {
    const url = result?.redirectUrl || '/';
    if (url.startsWith('/')) {
      navigate(url);
    } else {
      window.location.assign(url);
    }
  }

  const card = 'rounded-lg border border-gray-200 bg-white p-6 shadow-sm';

  return (
    <div className="flex justify-center px-4 py-8 sm:py-16">
      <div className="w-full max-w-md space-y-4">
        {/* ── Loading skeleton ── */}
        {pageState === 'loading' && (
          <div className="space-y-4" data-testid="track-claim-loading">
            <div className={card}>
              <Skeleton className="mb-3 h-4 w-24" />
              <Skeleton className="mb-2 h-6 w-40" />
              <Skeleton className="h-5 w-56" />
            </div>
            <div className={card}>
              <Skeleton className="mx-auto mb-3 h-8 w-32" />
              <Skeleton className="mx-auto mb-2 h-5 w-40" />
              <Skeleton className="mb-4 h-4 w-28" />
              <Skeleton className="mb-2 h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          </div>
        )}

        {/* ── Invalid / load-error ── */}
        {(pageState === 'invalid' || pageState === 'error') && (
          <div className={`${card} p-8 text-center`} data-testid="track-claim-error">
            <h1 className="text-xl font-semibold text-navy-900">
              {pageState === 'invalid' ? "This link isn't valid" : "Couldn't load your track"}
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {pageState === 'invalid'
                ? 'Check that you opened the most recent link from your email.'
                : 'Something went wrong on our end. Please try again in a moment.'}
            </p>
            <p className="mt-3 text-sm text-muted-foreground">
              Questions?{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-brand-ink hover:underline">
                {SUPPORT_EMAIL}
              </a>
            </p>
          </div>
        )}

        {/* ── Already claimed ── */}
        {pageState === 'claimed' && (
          <div className={`${card} p-8 text-center`} data-testid="track-claim-claimed">
            <h1 className="text-xl font-semibold text-navy-900">Already confirmed</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              This track has been claimed — you're all set.
            </p>
            <Button asChild className="mt-6 w-full bg-brand-blue !text-navy-900 hover:bg-brand-dark">
              <Link to={claim?.match.regatta ? `/regatta/${claim.match.regatta.slug}` : '/'}>
                Open RegattaReplay
              </Link>
            </Button>
          </div>
        )}

        {/* ── Expired ── */}
        {pageState === 'expired' && (
          <div className={`${card} p-8 text-center`} data-testid="track-claim-expired">
            <h1 className="text-xl font-semibold text-navy-900">This link has expired</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Claim links only work for a limited time. Reply to your upload email or send the
              track again and we'll issue a fresh link.
            </p>
            <p className="mt-3 text-sm text-muted-foreground">
              Need a hand?{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-brand-ink hover:underline">
                {SUPPORT_EMAIL}
              </a>
            </p>
          </div>
        )}

        {/* ── Success ── */}
        {pageState === 'success' && (
          <div className={`${card} p-8 text-center`} data-testid="track-claim-success">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
              <svg
                className="h-6 w-6 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-xl font-semibold text-navy-900">Your track is in</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Replays will appear here as they're assembled.
            </p>
            <Button
              onClick={goToRedirect}
              className="mt-6 w-full bg-brand-blue !text-navy-900 hover:bg-brand-dark"
              data-testid="success-continue-button"
            >
              View my replays
            </Button>
          </div>
        )}

        {/* ── Claim form ── */}
        {(pageState === 'ready' || pageState === 'claiming') && claim && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleConfirm();
            }}
            className="space-y-4"
            data-testid="track-claim-form"
          >
            {/* 1. You sent this */}
            <div className={card} data-testid="track-summary-card">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                You sent this
              </p>
              <p className="mt-2 text-lg font-semibold text-navy-900">
                {formatTrackDate(claim.track.trackDate)}
              </p>
              <p className="text-sm text-muted-foreground">
                {formatTrackDuration(claim.track.durationSec)} on the water
                {claim.track.sourceDevice ? ` · ${claim.track.sourceDevice}` : ''}
              </p>
              {claim.match.racesDetected.length > 0 && (
                <p className="mt-2 text-sm text-muted-foreground" data-testid="races-detected">
                  {claim.match.racesDetected.length === 1
                    ? '1 race'
                    : `${claim.match.racesDetected.length} races`}{' '}
                  detected on this day
                </p>
              )}
            </div>

            {/* 2. This is you? */}
            {hasRegatta ? (
              <div className={card}>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  This is you?
                </p>
                <p className="mt-1 text-sm font-medium text-navy-900">
                  {claim.match.regatta!.name}
                </p>

                {entryStep === 'confirm' && matchedEntry && (
                  <div className="mt-4 space-y-4" data-testid="entry-confirm">
                    <EntryCard entry={matchedEntry} />
                    <div className="space-y-2">
                      <Button
                        type="button"
                        className="w-full bg-brand-blue !text-navy-900 hover:bg-brand-dark"
                        onClick={() => {
                          setSelectedEntryId(matchedEntry.entryId);
                          setEntryStep('chosen');
                        }}
                        data-testid="entry-yes-button"
                      >
                        Yes, that's my boat
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full"
                        onClick={() => {
                          setSelectedEntryId(null);
                          setEntryStep('picker');
                        }}
                        data-testid="entry-no-button"
                      >
                        Not my boat
                      </Button>
                    </div>
                  </div>
                )}

                {entryStep === 'picker' && (
                  <div className="mt-4 space-y-3" data-testid="entry-picker">
                    <p className="text-sm text-muted-foreground">Pick your boat:</p>
                    {claim.entryOptions.length > 6 && (
                      <Input
                        value={entryFilter}
                        onChange={(e) => setEntryFilter(e.target.value)}
                        placeholder="Search sail number, boat, or skipper"
                        data-testid="entry-filter-input"
                      />
                    )}
                    <ul className="max-h-72 space-y-2 overflow-y-auto">
                      {filteredOptions.map((entry) => (
                        <li key={entry.entryId}>
                          <button
                            type="button"
                            className="w-full rounded-md border border-gray-200 px-4 py-3 text-left hover:border-brand-blue hover:bg-slate-50"
                            onClick={() => {
                              setSelectedEntryId(entry.entryId);
                              setEntryStep('chosen');
                            }}
                            data-testid={`entry-option-${entry.entryId}`}
                          >
                            <span className="block font-semibold text-navy-900">
                              {formatSailNumber(entry.sailNumber)} · {entry.boatName}
                            </span>
                            <span className="block text-sm text-muted-foreground">
                              {entry.skipperName}
                            </span>
                          </button>
                        </li>
                      ))}
                      {filteredOptions.length === 0 && (
                        <li className="py-2 text-center text-sm text-muted-foreground">
                          No boats match your search.
                        </li>
                      )}
                    </ul>
                  </div>
                )}

                {entryStep === 'chosen' && selectedEntry && (
                  <div className="mt-4 space-y-3" data-testid="entry-chosen">
                    <EntryCard entry={selectedEntry} />
                    <div className="text-center">
                      <button
                        type="button"
                        className="text-sm text-brand-ink hover:underline"
                        onClick={() => {
                          setSelectedEntryId(null);
                          setEntryStep('picker');
                        }}
                        data-testid="entry-change-button"
                      >
                        Change boat
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className={card} data-testid="manual-match-note">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Matching your track
                </p>
                <p className="mt-2 text-sm text-muted-foreground">
                  We couldn't automatically match this track to a regatta. Confirm below and our
                  team will match it manually — you'll get an email when it's placed.
                </p>
              </div>
            )}

            {/* 3. Phone verification — gated by VITE_ENABLE_PHONE_VERIFY build flag.
                 Hides the step on environments where Twilio / Turnstile are not yet
                 provisioned. The backend REQUIRE_PHONE_VERIFY flag is independent. */}
            {import.meta.env.VITE_ENABLE_PHONE_VERIFY === 'true' ? (
              phoneVerify === null ? (
                <div className={card} data-testid="phone-verify-card">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Race alerts
                  </p>
                  <div className="mt-3">
                    <PhoneVerificationStep
                      onVerified={(result) => setPhoneVerify(result)}
                      onSkip={() => setPhoneVerify({ notifyOptIn: false })}
                    />
                  </div>
                </div>
              ) : phoneVerify.verifyToken ? (
                // Verified: show green pill
                <div className={card} data-testid="phone-verify-done-card">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Race alerts
                  </p>
                  <div className="mt-2">
                    <PhoneVerificationStep
                      onVerified={(result) => setPhoneVerify(result)}
                      onSkip={() => setPhoneVerify({ notifyOptIn: false })}
                      existingVerifiedPhone="verified"
                    />
                    {phoneVerify.notifyOptIn && (
                      <p className="mt-1 text-xs text-green-700">
                        You'll receive race result notifications.
                      </p>
                    )}
                  </div>
                </div>
              ) : null /* skipped — no card */
            ) : null}

            {/* 4. Terms + optional name + CTA */}
            <div className={`${card} space-y-4`}>
              <div className="flex items-start gap-3">
                <Checkbox
                  id="accept-tos"
                  checked={acceptTos}
                  onCheckedChange={(v) => setAcceptTos(v === true)}
                  className="mt-0.5"
                  data-testid="tos-checkbox"
                />
                <Label htmlFor="accept-tos" className="text-sm font-normal leading-snug">
                  I accept the{' '}
                  <a
                    href="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-ink underline"
                  >
                    Terms of Service
                  </a>
                </Label>
              </div>

              <div>
                <Label htmlFor="display-name" className="sr-only">
                  Your name (optional)
                </Label>
                <Input
                  id="display-name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Your name (optional)"
                  data-testid="display-name-input"
                />
              </div>

              {submitError && (
                <p className="text-sm text-destructive" data-testid="claim-error">
                  {submitError}
                </p>
              )}

              <Button
                type="submit"
                className="w-full bg-brand-blue !text-navy-900 hover:bg-brand-dark"
                disabled={!canSubmit}
                data-testid="confirm-button"
              >
                {pageState === 'claiming' ? 'Confirming…' : 'Confirm my track'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
